/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { EnvironmentalImpactByApplication } from "../../models/http/application/environmental-impact-by-application.model";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { StrategicApplicationFileStatusResponse } from "../../models/http/strategic-applications/strategic-application-file-status-response.model";
import { StrategicApplicationFilesList } from "../../models/http/strategic-applications/strategic-application-files-list.model";
import { StrategicApplicationGeneralInformation } from "../../models/http/strategic-applications/strategic-application-general-information.model";
import { StrategicApplicationUploadResponse } from "../../models/http/strategic-applications/strategic-application-upload-response.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { isNullOrUndefined } from "../../utils/utils";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class StrategicApplicationsHttpService {
	private _strategicApplicationsOperations = ENDPOINT_GROUPS.strategicApplications.operations;

	constructor(private _communicationService: CommunicationService) {}

	public getFiles(tenantId: string): Observable<StrategicApplicationFilesList> {
		const pathParams = { tenantId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.getFiles.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	public uploadFile(
		tenantId: string,
		file: FormData,
		isTemporary?: boolean
	): Observable<StrategicApplicationUploadResponse> {
		const pathParams = { tenantId };
		const paramsObject: Record<string, string> = {};

		if (!isNullOrUndefined(isTemporary)) {
			paramsObject["isTemporary"] = String(isTemporary);
		}

		const options: { params?: HttpParams; pathParams: typeof pathParams } = { pathParams };

		if (Object.keys(paramsObject).length) {
			options.params = new HttpParams({ fromObject: paramsObject });
		}

		return this._communicationService.callService(
			this._strategicApplicationsOperations.uploadFile.service,
			HttpMethods.POST,
			options,
			file
		);
	}

	public getFileStatus(
		tenantId: string,
		fileId: string
	): Observable<StrategicApplicationFileStatusResponse> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.getFileStatus.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	public renameFile(tenantId: string, fileId: string, name: string): Observable<void> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.renameFile.service,
			HttpMethods.PUT,
			{ pathParams },
			{ name }
		);
	}

	public deleteFile(tenantId: string, fileId: string): Observable<void> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.deleteFile.service,
			HttpMethods.DELETE,
			{ pathParams }
		);
	}

	public exportFile(tenantId: string, fileId: string): Observable<string> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.exportFile.service,
			HttpMethods.POST,
			{ pathParams, responseType: "text" }
		);
	}

	public getGeneralInformation(
		tenantId: string,
		fileId: string
	): Observable<StrategicApplicationGeneralInformation> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._strategicApplicationsOperations.getGeneralInformation.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	public getEmissionApplications(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<BarChart>> {
		const pathParams = { tenantId, fileId };

		return this._communicationService
			.callService(
				this._strategicApplicationsOperations.getEmissionApplications.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionByScope(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<PieChart>> {
		const pathParams = { tenantId, fileId };

		return this._communicationService
			.callService(
				this._strategicApplicationsOperations.getEmissionByScope.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	public getEmissionByDeployment(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<PieChart>> {
		const pathParams = { tenantId, fileId };

		return this._communicationService
			.callService(
				this._strategicApplicationsOperations.getEmissionByDeployment.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	public getTotalEmissionsOvertime(
		tenantId: string,
		fileId: string,
		startDate: string,
		endDate?: string
	): Observable<ChartResponse<LineChart>> {
		const pathParams = { tenantId, fileId };
		const queryParams: Record<string, string> = { startDate };

		if (!isNullOrUndefined(endDate)) {
			queryParams["endDate"] = endDate as string;
		}

		const options: { params?: HttpParams; pathParams: typeof pathParams } = { pathParams };

		if (Object.keys(queryParams).length) {
			options.params = new HttpParams({ fromObject: queryParams });
		}

		return this._communicationService
			.callService(
				this._strategicApplicationsOperations.getTotalEmissionsOvertime.service,
				HttpMethods.GET,
				options
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public downloadTemplate(level: string): Observable<Blob> {
		return this._communicationService.callService(
			this._strategicApplicationsOperations.downloadTemplate.service,
			HttpMethods.GET,
			{ params: new HttpParams({ fromObject: { level } }), responseType: "blob" }
		);
	}

	public getEnvironmentalImpactByApplication(
		fileId: string,
		page = 0,
		size = 10
	): Observable<EnvironmentalImpactByApplication> {
		const pathParams = { fileId };
		const params = new HttpParams({
			fromObject: { page: page.toString(), size: size.toString() }
		});

		return this._communicationService.callService(
			this._strategicApplicationsOperations.getEnvironmentalImpactByApplication.service,
			HttpMethods.GET,
			{ pathParams, params }
		);
	}
}
